import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { orderWatchlists, readWatchlistIconOrder, writeWatchlistIconOrder } from '../lib/watchlistIconOrder.js'

export function useWatchlistIconOrder(userId, lists) {
  const [state, setState] = useState({ owner: null, order: [], ready: false, saving: false, error: null })
  const generation = useRef(0)
  const pending = useRef(false)
  const owner = useRef(userId)
  owner.current = userId

  const reload = useCallback(() => {
    const token = ++generation.current
    pending.current = false
    setState({ owner: userId, order: [], ready: false, saving: false, error: null })
    if (!userId) return
    readWatchlistIconOrder(userId).then(order => {
      if (token === generation.current && owner.current === userId) {
        setState({ owner: userId, order, ready: true, saving: false, error: null })
      }
    }).catch(() => {
      if (token === generation.current && owner.current === userId) {
        setState({ owner: userId, order: [], ready: false, saving: false, error: 'Could not load watchlist order.' })
      }
    })
  }, [userId])

  useEffect(() => {
    reload()
    return () => { generation.current += 1 }
  }, [reload])

  const orderedLists = useMemo(() => orderWatchlists(lists, state.owner === userId ? state.order : []), [lists, state.order, state.owner, userId])
  const save = useCallback(async listIds => {
    if (!userId || state.owner !== userId || !state.ready || pending.current) return
    // Store only a permutation of the current visible custom lists.
    const ids = orderedLists.map(list => list.listId)
    if (listIds.length !== ids.length || new Set(listIds).size !== ids.length || listIds.some(id => !ids.includes(id))) return
    const previous = state.order
    const token = generation.current
    pending.current = true
    setState(current => ({ ...current, order: listIds, saving: true, error: null }))
    try {
      await writeWatchlistIconOrder(userId, listIds)
      if (token === generation.current && owner.current === userId) setState(current => ({ ...current, saving: false }))
    } catch {
      if (token === generation.current && owner.current === userId) {
        setState(current => ({ ...current, order: previous, saving: false, error: 'Could not save watchlist order. Previous order restored; drag again to retry.' }))
      }
    } finally {
      if (token === generation.current) pending.current = false
    }
  }, [orderedLists, state.order, state.owner, state.ready, userId])

  return { lists: orderedLists, save, reload, ready: state.owner === userId && state.ready, saving: state.saving, error: state.owner === userId ? state.error : null }
}
